import React, { useState, useEffect } from 'react';
import { App } from '@capacitor/app';
import { Button } from 'react-bootstrap';
import Game from './Game';
import DeckManager from './DeckManager';
import Rules from './rules';
import Settings from './Settings';
import MultiplayerWrapper from './MultiplayerWrapper';
import AudioManager from '../audio/AudioManager';
import './Game.css';
import '../styles/accessibility.css';
import { CapacitorGameConnect } from '@openforge/capacitor-game-connect';

const HomeScreen = () => {
  const [currentScreen, setCurrentScreen] = useState('home');
  const [showSettings, setShowSettings] = useState(false);
  const [showIOSBanner, setShowIOSBanner] = useState(false);

  useEffect(() => {
	  const subscription = App.addListener('appStateChange', ({ isActive }) => {
		console.log('App state changed:', isActive ? 'active' : 'inactive');
		AudioManager.handleAppState(isActive);
	  });
  
	  // Start music when component mounts (if enabled)
	  AudioManager.playBackgroundMusic();
  
	  return () => {
		subscription.remove();
	  };
	}, []);

  useEffect(() => {
	const signIn = async () => {
	  try {
		await CapacitorGameConnect.signIn();
		console.log('User signed in successfully');
	  } catch (error) {
		console.error('An error occurred during sign-in:', error);
	  }
	};
	signIn();
  }, []);
  
  // Check if iOS web user
  useEffect(() => {
    const checkIOSWeb = () => {
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      const isWeb = typeof window !== 'undefined' && !window.Capacitor?.isNativePlatform();
      const bannerDismissed = localStorage.getItem('iosBannerDismissed');
      
      if (isIOS && isWeb && !bannerDismissed) {
        setShowIOSBanner(true);
      }
    };
    
    checkIOSWeb();
  }, []);

  // Handle screen changes
  const handleScreenChange = (screen) => {
	setCurrentScreen(screen);
	// Don't stop the music when changing screens
  };

  if (currentScreen === 'game') {
	return <Game onExit={() => handleScreenChange('home')} />;
  }

  if (currentScreen === 'deckManager') {
	return <DeckManager onExit={() => handleScreenChange('home')} />;
  }

  if (currentScreen === 'multiplayer') {
	return <MultiplayerWrapper onExit={() => handleScreenChange('home')} />;
  }

  const LeaderboardButton = ({ leaderboardID }) => {
	const [isWeb] = useState(typeof window !== 'undefined' && !window.Capacitor?.isNativePlatform());
	
	const handleClick = () => {
	  if (isWeb) {
		// Show coming soon message on web
		alert('Leaderboard coming soon for web version! Download the app for full features.');
		return;
	  }
	  
	  CapacitorGameConnect.showLeaderboard({ leaderboardID })
		.catch(error => console.error('Error showing leaderboard', error));
	};

	return (
	  <Button 
		onClick={handleClick} 
		variant="light" 
		size="lg"
		style={{ 
		  opacity: isWeb ? 0.7 : 1,
		  fontSize: 'clamp(14px, 3.5vw, 18px)',
		  padding: 'clamp(10px, 2.5vw, 16px) clamp(18px, 4vw, 28px)',
		  fontWeight: '600',
		  borderRadius: '12px',
		  transition: 'all 0.3s ease',
		}}
		aria-label={isWeb ? "View leaderboard - coming soon for web version" : "View high score leaderboard"}
		aria-disabled={isWeb}
	  >
		Leaderboard {isWeb && '(Coming Soon)'}
	  </Button>
	);
  };

  return (
	<div 
	  className="Home page-transition" 
	  role="main"
	  aria-label="Neolysium Card Game Home"
	  style={{
	    minHeight: '100vh',
	    textAlign: 'center',
	  }}
	>
	  {/* Skip to main content link for keyboard users */}
	  <a href="#main-menu" className="skip-link">
	    Skip to main menu
	  </a>
	  
	  {/* iOS App Store Banner */}
	  {showIOSBanner && (
	    <div 
	      role="banner"
	      aria-label="App Store promotion"
	      style={{
	        position: 'fixed',
	        top: 0,
	        left: 0,
	        right: 0,
	        background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.95) 0%, rgba(20, 20, 40, 0.95) 100%)',
	        borderBottom: '2px solid rgba(255, 215, 0, 0.3)',
	        padding: '15px',
	        zIndex: 2000,
	        display: 'flex',
	        alignItems: 'center',
	        justifyContent: 'space-between',
	        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
	      }}
	    >
	      <div style={{
	        display: 'flex',
	        alignItems: 'center',
	        gap: '15px',
	        flex: 1,
	      }}>
	        <img 
	          src="/icon.png" 
	          alt="Neolysium app icon" 
	          style={{
	            width: '50px',
	            height: '50px',
	            borderRadius: '12px',
	            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.3)',
	          }}
	        />
	        <div style={{ textAlign: 'left' }}>
	          <div style={{
	            fontSize: '16px',
	            fontWeight: 'bold',
	            color: '#fff',
	            marginBottom: '2px',
	          }}>
	            Neolysium
	          </div>
	          <div style={{
	            fontSize: '13px',
	            color: 'rgba(255, 255, 255, 0.8)',
	          }}>
	            Get the full experience on the App Store
	          </div>
	        </div>
	      </div>
	      <div style={{
	        display: 'flex',
	        gap: '10px',
	        alignItems: 'center',
	      }}>
	        <a
	          href="https://apps.apple.com/us/app/neolysium/id6471679998"
	          target="_blank"
	          rel="noopener noreferrer"
	          aria-label="View Neolysium on the App Store (opens in new tab)"
	          style={{
	            padding: '8px 20px',
	            backgroundColor: '#007AFF',
	            color: 'white',
	            borderRadius: '20px',
	            textDecoration: 'none',
	            fontSize: '14px',
	            fontWeight: 'bold',
	            transition: 'all 0.3s ease',
	            whiteSpace: 'nowrap',
	          }}
	          onMouseEnter={(e) => {
	            e.currentTarget.style.backgroundColor = '#0056b3';
	            e.currentTarget.style.transform = 'scale(1.05)';
	          }}
	          onMouseLeave={(e) => {
	            e.currentTarget.style.backgroundColor = '#007AFF';
	            e.currentTarget.style.transform = 'scale(1)';
	          }}
	        >
	          View
	        </a>
	        <button
	          onClick={() => {
	            setShowIOSBanner(false);
	            localStorage.setItem('iosBannerDismissed', 'true');
	          }}
	          aria-label="Dismiss App Store banner"
	          style={{
	            background: 'none',
	            border: 'none',
	            color: 'rgba(255, 255, 255, 0.6)',
	            fontSize: '20px',
	            cursor: 'pointer',
	            padding: '10px',
	            transition: 'all 0.3s ease',
	            minWidth: '44px',
	            minHeight: '44px',
	          }}
	          onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 1)'}
	          onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.6)'}
	        >
	          <span aria-hidden="true">×</span>
	        </button>
	      </div>
	    </div>
	  )}
	  
	  {/* Settings Button */}
	  <div style={{
		position: 'fixed',
		bottom: 'clamp(15px, 4vw, 25px)',
		right: 'clamp(15px, 4vw, 25px)',
		zIndex: 1000
	  }}>
		<Button
		  variant="light"
		  onClick={() => setShowSettings(true)}
		  aria-label="Open settings"
		  style={{
			width: 'clamp(48px, 12vw, 56px)',
			height: 'clamp(48px, 12vw, 56px)',
			borderRadius: '50%',
			padding: 0,
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			boxShadow: '0 4px 15px rgba(0, 0, 0, 0.3)',
		  }}
		>
		  <svg 
		    xmlns="http://www.w3.org/2000/svg" 
		    width="clamp(20, 5vw, 24)" 
		    height="clamp(20, 5vw, 24)" 
		    fill="currentColor" 
		    viewBox="0 0 16 16"
		    aria-hidden="true"
		    focusable="false"
		    style={{ width: 'clamp(20px, 5vw, 24px)', height: 'clamp(20px, 5vw, 24px)' }}
		  >
			<path d="M9.405 1.05c-.413-1.4-2.397-1.4-2.81 0l-.1.34a1.464 1.464 0 0 1-2.105.872l-.31-.17c-1.283-.698-2.686.705-1.987 1.987l.169.311c.446.82.023 1.841-.872 2.105l-.34.1c-1.4.413-1.4 2.397 0 2.81l.34.1a1.464 1.464 0 0 1 .872 2.105l-.17.31c-.698 1.283.705 2.686 1.987 1.987l.311-.169a1.464 1.464 0 0 1 2.105.872l.1.34c.413 1.4 2.397 1.4 2.81 0l.1-.34a1.464 1.464 0 0 1 2.105-.872l.31.17c1.283.698 2.686-.705 1.987-1.987l-.169-.311a1.464 1.464 0 0 1 .872-2.105l.34-.1c1.4-.413 1.4-2.397 0-2.81l-.34-.1a1.464 1.464 0 0 1-.872-2.105l.17-.31c.698-1.283-.705-2.686-1.987-1.987l-.311.169a1.464 1.464 0 0 1-2.105-.872zM8 10.93a2.929 2.929 0 1 1 0-5.86 2.929 2.929 0 0 1 0 5.858z"/>
		  </svg>
		</Button>
	  </div>

	  <main 
	    id="main-menu"
	    style={{
	      display: 'flex',
	      flexDirection: 'column',
	      alignItems: 'center',
	      justifyContent: 'center',
	      minHeight: '100vh',
	      padding: '20px',
	      boxSizing: 'border-box',
	    }}
	  >
		<div className="text-center" style={{ width: '100%', maxWidth: '400px' }}>
		  <h1 style={{ 
		    margin: '0 auto 10px auto', 
		    color: 'white', 
		    fontSize: 'clamp(32px, 8vw, 48px)',
		    fontWeight: 'bold',
		    textShadow: '0 2px 20px rgba(255, 215, 0, 0.3)',
		  }}>
			Neolysium
		  </h1>
		  <p style={{
		    color: 'rgba(255, 255, 255, 0.6)',
		    fontSize: 'clamp(12px, 3vw, 16px)',
		    marginBottom: '30px',
		  }}>
		    A strategic card game
		  </p>
		  <p className="sr-only">Choose a game mode below.</p>
		  
		  <nav aria-label="Main menu">
		    <div 
		      className="d-grid gap-3" 
		      style={{ 
		        width: '100%',
		        maxWidth: '280px', 
		        margin: '0 auto',
		        padding: '0 10px',
		      }}
		    >
			  <Button 
			    onClick={() => handleScreenChange('game')}
			    variant="light"
			    size="lg"
			    aria-label="Play Solitaire mode - single player game"
			    style={{
			      fontSize: 'clamp(16px, 4vw, 20px)',
			      padding: 'clamp(12px, 3vw, 18px) clamp(20px, 5vw, 30px)',
			      fontWeight: '600',
			      borderRadius: '12px',
			      transition: 'all 0.3s ease',
			    }}
			  >
			    Solitaire
			  </Button>

			  <Button 
			    onClick={() => handleScreenChange('multiplayer')}
			    variant="light"
			    size="lg"
			    aria-label="Play Versus mode - compete against AI"
			    style={{
			      fontSize: 'clamp(16px, 4vw, 20px)',
			      padding: 'clamp(12px, 3vw, 18px) clamp(20px, 5vw, 30px)',
			      fontWeight: '600',
			      borderRadius: '12px',
			      transition: 'all 0.3s ease',
			    }}
			  >
			    Versus
			  </Button>

			  <Button 
			    onClick={() => handleScreenChange('deckManager')}
			    variant="light"
			    size="lg"
			    aria-label="Manage your card decks"
			    style={{
			      fontSize: 'clamp(16px, 4vw, 20px)',
			      padding: 'clamp(12px, 3vw, 18px) clamp(20px, 5vw, 30px)',
			      fontWeight: '600',
			      borderRadius: '12px',
			      transition: 'all 0.3s ease',
			    }}
			  >
			    Decks
			  </Button>
			  
			  <div style={{ marginTop: 'clamp(15px, 4vw, 30px)' }} aria-hidden="true"></div>
			  
			  <Rules />
			  <LeaderboardButton leaderboardID="allTimeHighScore" />
		    </div>
		  </nav>
		</div>
	  </main>

	  <Settings
		show={showSettings}
		onHide={() => setShowSettings(false)}
	  />
	</div>
  );
};

export default HomeScreen;